import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import BreadcrumbStructuredData from "@/components/BreadcrumbStructuredData";
import Breadcrumb from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Calculator, FileText, Cog, ArrowRight, CheckCircle, X, Check, Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, Link } from "react-router-dom";
import { supportedLanguages, type SupportedLanguage } from "@/lib/i18n";
import { generateLocalizedUrl, type PageSlug } from "@/lib/slugUtils";
import FilloutButton from "@/components/FilloutButton";

const Services = () => {
  const { t, ready } = useTranslation(['services', 'common']);
  const navigate = useNavigate();
  const { lang } = useParams<{ lang: SupportedLanguage }>();
  const currentLang = lang && supportedLanguages.includes(lang) ? lang : 'en';
  
  if (!ready) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-lg text-muted-foreground">Loading translations...</p>
        </div>
      </div>
    );
  }
  
  const mainServices = [
    {
      icon: Calculator,
      title: t('services:mainServices.0.title'),
      description: t('services:mainServices.0.description'),
      features: [
        t('services:mainServices.0.features.0'),
        t('services:mainServices.0.features.1'),
        t('services:mainServices.0.features.2'),
        t('services:mainServices.0.features.3'),
        t('services:mainServices.0.features.4')
      ],
      link: "/services/price-calculators"
    },
    {
      icon: FileText,
      title: t('services:mainServices.1.title'),
      description: t('services:mainServices.1.description'),
      features: [
        t('services:mainServices.1.features.0'),
        t('services:mainServices.1.features.1'),
        t('services:mainServices.1.features.2'),
        t('services:mainServices.1.features.3'),
        t('services:mainServices.1.features.4')
      ],
      link: "/services/complex-forms"
    },
    {
      icon: Cog,
      title: t('services:mainServices.2.title'),
      description: t('services:mainServices.2.description'),
      features: [
        t('services:mainServices.2.features.0'),
        t('services:mainServices.2.features.1'),
        t('services:mainServices.2.features.2'),
        t('services:mainServices.2.features.3'),
        t('services:mainServices.2.features.4')
      ],
      link: "/services/workflow-automation"
    }
  ];

  const industryServices = {
    craftsmen: [
      { title: t('services:industryServices.craftsmen.0.title'), description: t('services:industryServices.craftsmen.0.description') },
      { title: t('services:industryServices.craftsmen.1.title'), description: t('services:industryServices.craftsmen.1.description') },
      { title: t('services:industryServices.craftsmen.2.title'), description: t('services:industryServices.craftsmen.2.description') },
      { title: t('services:industryServices.craftsmen.3.title'), description: t('services:industryServices.craftsmen.3.description') }
    ],
    restaurants: [
      { title: t('services:industryServices.restaurants.0.title'), description: t('services:industryServices.restaurants.0.description') },
      { title: t('services:industryServices.restaurants.1.title'), description: t('services:industryServices.restaurants.1.description') },
      { title: t('services:industryServices.restaurants.2.title'), description: t('services:industryServices.restaurants.2.description') },
      { title: t('services:industryServices.restaurants.3.title'), description: t('services:industryServices.restaurants.3.description') }
    ],
    financial: [
      { title: t('services:industryServices.financial.0.title'), description: t('services:industryServices.financial.0.description') },
      { title: t('services:industryServices.financial.1.title'), description: t('services:industryServices.financial.1.description') },
      { title: t('services:industryServices.financial.2.title'), description: t('services:industryServices.financial.2.description') },
      { title: t('services:industryServices.financial.3.title'), description: t('services:industryServices.financial.3.description') }
    ],
    realestate: [
      { title: t('services:industryServices.realestate.0.title'), description: t('services:industryServices.realestate.0.description') },
      { title: t('services:industryServices.realestate.1.title'), description: t('services:industryServices.realestate.1.description') },
      { title: t('services:industryServices.realestate.2.title'), description: t('services:industryServices.realestate.2.description') },
      { title: t('services:industryServices.realestate.3.title'), description: t('services:industryServices.realestate.3.description') }
    ],
    ecommerce: [
      { title: t('services:industryServices.ecommerce.0.title'), description: t('services:industryServices.ecommerce.0.description') },
      { title: t('services:industryServices.ecommerce.1.title'), description: t('services:industryServices.ecommerce.1.description') },
      { title: t('services:industryServices.ecommerce.2.title'), description: t('services:industryServices.ecommerce.2.description') },
      { title: t('services:industryServices.ecommerce.3.title'), description: t('services:industryServices.ecommerce.3.description') }
    ]
  };

  const successStories = [
    {
      industry: t('services:successStories.0.industry'),
      company: t('services:successStories.0.company'),
      result: t('services:successStories.0.result'),
      description: t('services:successStories.0.description'),
      icon: "🏗️"
    },
    {
      industry: t('services:successStories.1.industry'), 
      company: t('services:successStories.1.company'),
      result: t('services:successStories.1.result'),
      description: t('services:successStories.1.description'),
      icon: "🍝"
    },
    {
      industry: t('services:successStories.2.industry'),
      company: t('services:successStories.2.company'), 
      result: t('services:successStories.2.result'),
      description: t('services:successStories.2.description'),
      icon: "🏠"
    }
  ];

  const processSteps = [
    {
      step: "01",
      title: t('services:process.0.title'),
      description: t('services:process.0.description')
    },
    {
      step: "02", 
      title: t('services:process.1.title'),
      description: t('services:process.1.description')
    },
    {
      step: "03",
      title: t('services:process.2.title'),
      description: t('services:process.2.description')
    },
    {
      step: "04",
      title: t('services:process.3.title'), 
      description: t('services:process.3.description')
    }
  ];

  const pricingTiers = [
    {
      name: t('services:pricing.0.name'),
      customization: t('services:pricing.0.customization'),
      setupTime: t('services:pricing.0.setupTime'), 
      cost: t('services:pricing.0.cost'),
      support: t('services:pricing.0.support'),
      integration: t('services:pricing.0.integration'),
      updates: t('services:pricing.0.updates'),
      recommended: false
    },
    {
      name: t('services:pricing.1.name'),
      customization: t('services:pricing.1.customization'),
      setupTime: t('services:pricing.1.setupTime'),
      cost: t('services:pricing.1.cost'), 
      support: t('services:pricing.1.support'),
      integration: t('services:pricing.1.integration'),
      updates: t('services:pricing.1.updates'),
      recommended: true
    },
    {
      name: t('services:pricing.2.name'),
      customization: t('services:pricing.2.customization'),
      setupTime: t('services:pricing.2.setupTime'),
      cost: t('services:pricing.2.cost'),
      support: t('services:pricing.2.support'), 
      integration: t('services:pricing.2.integration'),
      updates: t('services:pricing.2.updates'),
      recommended: false
    }
  ];

  const faqs = [
    {
      question: t('services:faq.0.question'),
      answer: t('services:faq.0.answer')
    },
    {
      question: t('services:faq.1.question'),
      answer: t('services:faq.1.answer')
    },
    {
      question: t('services:faq.2.question'),
      answer: t('services:faq.2.answer')
    },
    {
      question: t('services:faq.3.question'),
      answer: t('services:faq.3.answer')
    },
    {
      question: t('services:faq.4.question'),
      answer: t('services:faq.4.answer')
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead page="services" />
      <BreadcrumbStructuredData />
      <Header />
      
      {/* Breadcrumb Navigation */}
      <div className="container mx-auto px-4 py-4">
        <Breadcrumb />
      </div>
      
      {/* Hero Section */}
      <section className="py-20 bg-gradient-hero">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Badge variant="outline" className="mb-4 border-primary-foreground text-primary-foreground">
              {t('services:hero.badge')}
            </Badge>
            <h1 className="text-5xl md:text-6xl font-bold text-primary-foreground mb-6">
              {t('services:hero.title')}
            </h1>
            <p className="text-xl text-primary-foreground/90 mb-12 max-w-2xl mx-auto">
              {t('services:hero.description')}
            </p>
          </div>
        </div>
      </section>

      {/* My Offerings Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4">{t('services:offerings.badge')}</Badge>
            <h2 className="text-4xl font-bold mb-6">{t('services:offerings.title')}</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {t('services:offerings.description')}
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            {mainServices.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className="hover:shadow-elegant transition-smooth group h-full">
                  <CardHeader>
                    <div className="flex items-center justify-center w-16 h-16 bg-accent/10 rounded-lg mb-4 mx-auto group-hover:bg-accent group-hover:text-accent-foreground transition-smooth">
                      <IconComponent className="w-8 h-8 text-accent group-hover:text-accent-foreground" />
                    </div>
                    <CardTitle className="text-xl text-center">{service.title}</CardTitle>
                    <CardDescription className="text-center leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 mb-6">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4 text-accent flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <Button 
                      variant="outline" 
                      className="w-full group"
                      onClick={() => navigate(generateLocalizedUrl('portfolio', currentLang))}
                    >
                      {t('common:buttons.learnMore')}
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industry Solutions */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4">{t('services:industrySection.badge')}</Badge>
            <h2 className="text-4xl font-bold mb-6">{t('services:industrySection.title')}</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {t('services:industrySection.description')}
            </p>
          </div>
          
          <div className="max-w-5xl mx-auto">
            <Tabs defaultValue="craftsmen" className="w-full">
              <TabsList className="grid w-full grid-cols-5">
                <TabsTrigger value="craftsmen">{t('services:industryTabs.craftsmen')}</TabsTrigger>
                <TabsTrigger value="restaurants">{t('services:industryTabs.restaurants')}</TabsTrigger>
                <TabsTrigger value="financial">{t('services:industryTabs.financial')}</TabsTrigger>
                <TabsTrigger value="realestate">{t('services:industryTabs.realestate')}</TabsTrigger>
                <TabsTrigger value="ecommerce">{t('services:industryTabs.ecommerce')}</TabsTrigger>
              </TabsList>
              
              {Object.entries(industryServices).map(([key, services]) => {
                const getIndustrySlug = (tabKey: string): PageSlug => {
                  switch(tabKey) {
                    case 'realestate': return 'industry-real-estate';
                    case 'craftsmen': return 'industry-agencies';
                    case 'restaurants': return 'industry-restaurants';
                    case 'financial': return 'industry-financial';
                    case 'ecommerce': return 'industry-ecommerce';
                    default: return 'services';
                  }
                };

                return (
                  <TabsContent key={key} value={key} className="mt-8">
                    <div className="grid md:grid-cols-2 gap-6">
                      {services.map((service, index) => (
                        <Card key={index} className="hover:shadow-lg transition-smooth">
                          <CardHeader>
                            <CardTitle className="text-lg">{service.title}</CardTitle>
                            <CardDescription>{service.description}</CardDescription>
                          </CardHeader>
                        </Card>
                      ))}
                    </div>
                    <div className="mt-8 text-center">
                      <Link 
                        to={generateLocalizedUrl(getIndustrySlug(key), currentLang)}
                        data-testid={`link-industry-${key}`}
                      >
                        <Button variant="outline" size="lg">
                          {t('common:buttons.learnMore')} <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </TabsContent>
                );
              })}
            </Tabs>
          </div>
        </div>
      </section>

      {/* Pricing Comparison */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4">{t('services:features.badge')}</Badge>
            <h2 className="text-4xl font-bold mb-6">{t('services:features.title')}</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {t('services:features.description')}
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto overflow-x-auto">
            <div className="min-w-full">
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div className="font-semibold text-lg"></div>
                {pricingTiers.map((tier, index) => (
                  <div key={index} className="text-center">
                    <div className={`p-4 rounded-lg ${tier.recommended ? 'bg-accent text-accent-foreground' : 'bg-muted'}`}>
                      <h3 className="font-semibold">{tier.name}</h3>
                      {tier.recommended && (
                        <Badge variant="secondary" className="mt-2">{t('services:comparison.recommended')}</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              {[
                { label: t('services:comparison.customization'), key: "customization" },
                { label: t('services:comparison.setupTime'), key: "setupTime" },
                { label: t('services:comparison.cost'), key: "cost" },
                { label: t('services:comparison.support'), key: "support" },
                { label: t('services:comparison.integration'), key: "integration" },
                { label: t('services:comparison.updates'), key: "updates" }
              ].map((row, rowIndex) => (
                <div key={rowIndex} className="grid grid-cols-4 gap-4 py-3 border-b border-muted">
                  <div className="font-medium">{row.label}</div>
                  {pricingTiers.map((tier, index) => (
                    <div key={index} className="text-center text-sm">
                      {tier[row.key as keyof typeof tier]}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
          
          <div className="text-center mt-8">
            <Button 
              size="lg"
              onClick={() => navigate(generateLocalizedUrl('contact', currentLang))}
            >
              {t('common:buttons.learnMore')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Success Stories */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4">{t('services:successStoriesSection.badge')}</Badge>
            <h2 className="text-4xl font-bold mb-6">{t('services:successStoriesSection.title')}</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {t('services:successStoriesSection.description')}
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {successStories.map((story, index) => (
              <Card key={index} className="hover:shadow-elegant transition-smooth">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-4">
                    <span className="text-2xl">{story.icon}</span>
                    <Badge variant="secondary">{story.industry}</Badge>
                  </div>
                  <CardTitle className="text-xl">{story.company}</CardTitle>
                  <div className="text-2xl font-bold text-accent">{story.result}</div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="mb-4">{story.description}</CardDescription>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => navigate(generateLocalizedUrl('portfolio', currentLang))}
                  >
                    {t('common:buttons.learnMore')}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4">{t('services:processSection.badge')}</Badge>
            <h2 className="text-4xl font-bold mb-6">{t('services:processSection.title')}</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {t('services:processSection.description')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <Card key={index} className="text-center hover:shadow-elegant transition-smooth">
                <CardHeader>
                  <div className="text-3xl font-bold text-accent mb-4">{step.step}</div>
                  <CardTitle className="text-lg">{step.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-center">
                    {step.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4">{t('services:faqSection.badge')}</Badge>
            <h2 className="text-4xl font-bold mb-6">{t('services:faqSection.title')}</h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {t('services:faqSection.description')}
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-hero">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-primary-foreground mb-6">
              {t('services:cta.title')}
            </h2>
            
            <p className="text-xl text-primary-foreground/90 mb-12 max-w-2xl mx-auto">
              {t('services:cta.description')}
            </p>
            
            <FilloutButton 
              size="lg" 
              variant="secondary" 
              className="text-lg px-8 py-3 group"
            >
              {t('common:buttons.scheduleConsultation')}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </FilloutButton>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;